import {
  type AiResult,
  BASIC_QUOTE_FOOTER,
  type Branding,
  DEFAULT_VALIDITY_DAYS,
  defaultQuoteTerms,
  type FeatureFlags,
  type MeasuredLegendRow,
  type QuoteData,
  type QuoteLines,
  type QuoteTemplate,
  quoteDates,
  quoteFileName,
  quoteLineItems,
  quoteNumber,
  type TimePreset,
  type Units,
} from '@better-lights/core';
import { formatDate } from '../lib/format.ts';
import type { ProjectFile } from '../storage/record.ts';

/** basic (Free) · branded (Starter+: `branded_quote`) · templates (Pro+: `quote_templates`). docs/spec/08 §3. */
export type QuoteTier = 'basic' | 'branded' | 'templates';

export const LETTER: [number, number] = [612, 792];
export const A4: [number, number] = [595.28, 841.89];
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);

/** US Letter by default, A4 when the device region uses metric. */
export function pageSizeFor(locale: string): [number, number] {
  const region = (() => {
    try {
      return new Intl.Locale(locale).maximize().region ?? 'US';
    } catch {
      return 'US';
    }
  })();
  return IMPERIAL_REGIONS.has(region) ? LETTER : A4;
}

export function quoteTier(flags: FeatureFlags): QuoteTier {
  if (flags.quote_templates) return 'templates';
  if (flags.branded_quote) return 'branded';
  return 'basic';
}

export interface QuoteInput {
  file: ProjectFile;
  rows: MeasuredLegendRow[];
  currency: string;
  units: Units;
  flags: FeatureFlags;
  /** Personal branding (Starter+) or the org's company branding (Team). */
  branding: Branding | null;
  user: { email?: string | null; name?: string | null } | null;
  date: Date;
  locale: string;
  /** Pro+: add a page with the latest AI render per time of day. */
  includeAiRenders?: boolean;
}

export interface QuoteModel {
  tier: QuoteTier;
  template: QuoteTemplate;
  page: [number, number];
  accent: string;
  logo: string | null;
  business: string[];
  number: string;
  date: string;
  validUntil: string;
  projectName: string;
  customer: string[];
  lines: QuoteLines;
  notes: string | null;
  terms: string;
  footer: string | null;
  aiRenders: AiResult[];
  fileName: string;
}

const NEUTRAL_ACCENT = '#1C1C1E';
const DEFAULT_ACCENT = '#FFB547';
const ORDER: TimePreset[] = ['day', 'evening', 'night'];

/** Latest render per time of day (Day, Evening, Night order), 0–3 results. */
export function latestRenders(aiResults: AiResult[]): AiResult[] {
  const out: AiResult[] = [];
  for (const t of ORDER) {
    const r = aiResults.find((a) => a.time === t); // newest first
    if (r) out.push(r);
  }
  return out;
}

export function quoteDataOf(file: ProjectFile): QuoteData {
  const q = (file as ProjectFile & { quote?: QuoteData }).quote;
  return q && typeof q === 'object' ? q : {};
}

export function buildQuoteModel(input: QuoteInput): QuoteModel {
  const { file, flags } = input;
  const tier = quoteTier(flags);
  const q = quoteDataOf(file);
  const b = tier === 'basic' ? null : input.branding;
  const template: QuoteTemplate = tier === 'templates' ? (q.template ?? b?.template ?? 'classic') : 'classic';
  const validity = b?.validityDays && b.validityDays > 0 ? b.validityDays : DEFAULT_VALIDITY_DAYS;
  const depositPercent = tier === 'templates' ? (q.depositPercent ?? b?.depositPercent ?? null) : null;
  const projectName = file.name || formatDate(file.createdAt);
  const business =
    tier === 'basic'
      ? [input.user?.name, input.user?.email].filter((x): x is string => !!x)
      : [
          b?.companyName,
          b?.phone,
          b?.email,
          b?.website,
          b?.address,
          b?.licenseNumber ? `License ${b.licenseNumber}` : null,
        ].filter((x): x is string => !!x && x.trim() !== '');
  const c = q.customer ?? {};
  const dates = quoteDates(input.date, validity);
  return {
    tier,
    template,
    page: pageSizeFor(input.locale),
    accent: tier === 'basic' ? NEUTRAL_ACCENT : (b?.accentColor ?? DEFAULT_ACCENT),
    logo: b?.logo ?? null,
    business,
    number: quoteNumber(file.id, input.date),
    date: dates.date,
    validUntil: dates.validUntil,
    projectName,
    customer: [c.name, c.address, c.email, c.phone].filter((x): x is string => !!x && x.trim() !== ''),
    lines: quoteLineItems(input.rows, input.currency, {
      calibrated: file.doc.frameWidthFt != null,
      depositPercent,
      units: input.units,
    }),
    notes: q.notes?.trim() ? q.notes.trim() : null,
    terms: b?.terms?.trim() ? b.terms.trim() : defaultQuoteTerms(validity),
    footer: tier === 'basic' ? BASIC_QUOTE_FOOTER : null,
    aiRenders: tier === 'templates' && input.includeAiRenders ? latestRenders(file.aiResults) : [],
    fileName: quoteFileName(projectName, input.date),
  };
}
